import React from "react";

// YOUR TASTE SIGNAL — a compact bipolar "fingerprint" built ONLY from existing
// derived affinity evidence (profile.tasteSignal). No scores, no percentages,
// no axes: each row is a pole-to-pole spectrum with a small delta marker showing
// where the player's kept choices consistently land. Left pole carries a faint
// signal-red, right pole a faint signal-blue — the TASTE TEST confrontation
// grammar, restrained. Built as a reusable component so the same visual grammar
// can later power longitudinal evolution / share snapshot / match comparison.

function SignalRow({ row, onSignal, active, onToggle }) {
  const value = Math.max(-1, Math.min(1, row.value || 0));
  // map [-1,1] -> [8%,92%] left(low) -> right(high); keep marker off the edges
  const pct = 8 + ((value + 1) / 2) * 84;
  const leansHigh = value >= 0;
  const markerColor = leansHigh ? "var(--tt-right)" : "var(--tt-left)";
  const markerGlow = leansHigh ? "rgba(0,229,255,0.55)" : "rgba(255,42,85,0.55)";
  const clickable = typeof onSignal === "function";

  const body = (
    <>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span
          className={`font-mono text-[9.5px] tracking-[0.16em] uppercase leading-none ${
            leansHigh ? "text-white/50" : "text-bone/90"
          }`}
        >
          {row.lowLabel}
        </span>
        <span
          className={`font-mono text-[9.5px] tracking-[0.16em] uppercase leading-none text-right ${
            leansHigh ? "text-bone/90" : "text-white/50"
          }`}
        >
          {row.highLabel}
        </span>
      </div>
      <div
        className="relative h-[6px] rounded-full"
        style={{
          background:
            "linear-gradient(90deg, rgba(255,42,85,0.30), rgba(255,255,255,0.09) 50%, rgba(0,229,255,0.30))",
        }}
      >
        <span aria-hidden className="absolute top-1/2 left-1/2 h-2 w-px -translate-x-1/2 -translate-y-1/2 bg-white/15" />
        <span aria-hidden className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ left: `${pct}%` }}>
          <span className="block h-[11px] w-[11px] rotate-45 rounded-[2px]" style={{ background: markerColor, boxShadow: `0 0 10px ${markerGlow}` }} />
        </span>
      </div>
    </>
  );

  if (!clickable) {
    return <div className="select-none" data-testid={`taste-signal-row-${row.key}`}>{body}</div>;
  }
  return (
    <div className="select-none" data-testid={`taste-signal-row-${row.key}`}>
      <button type="button" onClick={onToggle} className="w-full text-left focus:outline-none">
        {body}
      </button>
      {active && (
        <button
          type="button"
          data-testid={`see-the-evidence-${row.key}`}
          onClick={() => onSignal(row.key)}
          className="mt-2 font-mono text-[9.5px] font-bold tracking-[0.18em] uppercase hover:opacity-80"
          style={{ color: "var(--tt-right)" }}
        >
          See the evidence →
        </button>
      )}
    </div>
  );
}

export default function TasteSignal({ rows = [], heading = "Your Taste Signal", onSignal }) {
  const [activeKey, setActiveKey] = React.useState(null);
  // LESS, BUT STRONGER — omit entirely unless the evidence supports a real map.
  if (!Array.isArray(rows) || rows.length < 3) return null;
  return (
    <div data-testid="profile-taste-signal">
      {heading && (
        <p className="font-mono text-[10px] font-bold tracking-[0.28em] uppercase text-subtle mb-4">
          {heading}
        </p>
      )}
      <div className="rounded-2xl border border-white/10 bg-white/[0.035] px-4 py-4 space-y-4">
        {rows.slice(0, 6).map((r) => (
          <SignalRow
            key={r.key}
            row={r}
            onSignal={onSignal}
            active={activeKey === r.key}
            onToggle={() => setActiveKey(activeKey === r.key ? null : r.key)}
          />
        ))}
      </div>
    </div>
  );
}
